import React from "react";
import { Box, Chip, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { Add, CardGiftcard, DeleteOutline, Remove } from "@mui/icons-material";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { Product } from "../../../types";
import { formatCurrencyBDT } from "../../../utils/currency";
import { ProductThumbnail } from "../../common/ProductThumbnail";
import { ComboPriceHint } from "../../orders/ComboPriceHint";
import { OrderFormData, OrderFormLine } from "./orderFormSchema";

const MAX_QTY = 9999;

interface Props {
    index: number;
    line: OrderFormLine;
    product: Partial<Product> | undefined;
    control: Control<OrderFormData>;
    errors: FieldErrors<OrderFormData>;
    /** Sets the quantity (and re-prices a line still on its automatic price). */
    onQuantity: (index: number, quantity: number) => void;
    onUnitPrice: (index: number, price: number) => void;
    onRemove: (index: number) => void;
    /** Why removing is blocked, if it is. */
    removeBlocked?: string;
    /** Units this order already holds (edit mode), so they don't count as short. */
    alreadyReserved: number;
    /** Drafts take no stock, so they get no stock warning. */
    checkStock: boolean;
    /** "Customer said …" from a JSON import. */
    hint?: string;
}

/** One order line: photo, name, stock, − / + quantity, price, total. */
export const OrderLineRow: React.FC<Props> = ({
    index,
    line,
    product,
    control,
    errors,
    onQuantity,
    onUnitPrice,
    onRemove,
    removeBlocked,
    alreadyReserved,
    checkStock,
    hint,
}) => {
    const isGift = !!line.is_gift;
    const qty = Number(line.quantity) || 0;
    const price = Number(line.unit_price) || 0;
    const lineErrors = errors.items?.[index];

    // Stock: what's free, plus what this order already holds.
    const stock = product?.stock;
    const available =
        stock != null ? Number(stock.quantity) - Number(stock.reserved_quantity || 0) + alreadyReserved : null;
    const short = checkStock && available != null && qty > available;
    const stockText =
        available == null
            ? null
            : short
              ? available <= 0
                  ? "Out of stock"
                  : `Only ${available} in stock`
              : `${available} in stock`;

    return (
        <Box
            sx={{
                py: 1.25,
                borderBottom: 1,
                borderColor: "divider",
                "&:last-of-type": { borderBottom: 0 },
            }}
        >
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                <ProductThumbnail images={product?.images} alt={product?.name} size={44} />
                <Box sx={{ flex: "1 1 160px", minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                            {product?.name || "Loading…"}
                        </Box>
                        {isGift && (
                            <Chip
                                icon={<CardGiftcard />}
                                label="Gift"
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ height: 20, "& .MuiChip-label": { px: 0.75 } }}
                            />
                        )}
                    </Typography>
                    {stockText && (
                        <Typography variant="caption" color={short ? "warning.main" : "text.secondary"} fontWeight={short ? 600 : 400}>
                            {stockText}
                        </Typography>
                    )}
                    {!isGift && (
                        <ComboPriceHint
                            product={product as Product | undefined}
                            quantity={qty}
                            unitPrice={price}
                            onUse={(p) => onUnitPrice(index, p)}
                        />
                    )}
                    {hint && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            Customer said: “{hint}”
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: { xs: "auto", sm: 0 }, flexShrink: 0 }}>
                    {/* Quantity */}
                    <Box sx={{ display: "flex", alignItems: "center", border: 1, borderColor: "divider", borderRadius: 1 }}>
                        <IconButton
                            size="small"
                            aria-label="One less"
                            disabled={qty <= 1}
                            onClick={() => onQuantity(index, Math.max(1, qty - 1))}
                        >
                            <Remove fontSize="small" />
                        </IconButton>
                        <Controller
                            name={`items.${index}.quantity`}
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    value={field.value ?? ""}
                                    onChange={(e) => {
                                        const n = Number(e.target.value);
                                        if (e.target.value !== "" && Number.isFinite(n) && n > 0) {
                                            onQuantity(index, Math.min(MAX_QTY, Math.floor(n)));
                                        } else {
                                            field.onChange(e.target.value === "" ? "" : n);
                                        }
                                    }}
                                    onBlur={field.onBlur}
                                    variant="standard"
                                    type="number"
                                    error={!!lineErrors?.quantity}
                                    inputProps={{
                                        min: 1,
                                        "aria-label": "Quantity",
                                        style: { textAlign: "center", width: 40, padding: "4px 0" },
                                    }}
                                    InputProps={{ disableUnderline: true }}
                                />
                            )}
                        />
                        <IconButton
                            size="small"
                            aria-label="One more"
                            onClick={() => onQuantity(index, Math.min(MAX_QTY, qty + 1))}
                        >
                            <Add fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Unit price */}
                    {isGift ? (
                        <Typography variant="body2" color="secondary.main" fontWeight={600} sx={{ width: 104, textAlign: "center" }}>
                            Free
                        </Typography>
                    ) : (
                        <Controller
                            name={`items.${index}.unit_price`}
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    value={field.value ?? ""}
                                    type="number"
                                    size="small"
                                    error={!!lineErrors?.unit_price}
                                    inputProps={{ min: 0, step: 1, "aria-label": "Price each" }}
                                    InputProps={{ startAdornment: <InputAdornment position="start">৳</InputAdornment> }}
                                    sx={{ width: 104 }}
                                />
                            )}
                        />
                    )}

                    <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ width: 84, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                    >
                        {isGift ? "Free" : formatCurrencyBDT(qty * price, { minimumFractionDigits: 0 })}
                    </Typography>

                    <Tooltip title={removeBlocked ?? "Remove"}>
                        <span>
                            <IconButton
                                size="small"
                                color="error"
                                aria-label={`Remove ${product?.name ?? "line"}`}
                                disabled={!!removeBlocked}
                                onClick={() => onRemove(index)}
                            >
                                <DeleteOutline fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
};

export default OrderLineRow;
